var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a, _b, _c;
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { GoalPlannerTimeslot } from '@/models/GoalPlannerTimeslot';
import { DateTime } from 'luxon';
import ErrorHandler from '@/support/ErrorHandler';
import { RpcClient } from '@/http';
import { GroupTemplateFilters } from '@/views/Groups/Tabs/GoalPlanner/GoalPlanner';
import { Group } from '@/models/Group';
import { AQuillEditorActionsEnum } from '@/components/AQuillEditor/AQuillEditor';
import AQuillEditor from '@/components/AQuillEditor/AQuillEditor.vue';
import SubjectDialog from '@/components/SubjectDialog/SubjectDialog.vue';
let ManageTimeslotDialog = class ManageTimeslotDialog extends Vue {
    constructor() {
        // #region @VModel
        // #endregion
        super(...arguments);
        // #endregion
        // #region @Refs
        // #endregion
        // #region Class properties
        this.isLoading = true;
        this.isLoadingGoals = false;
        // We store the goals separately to be able to update the list when a goal is removed
        // This improves UX if user wants to remove multiple goals in a row
        this.goals = [];
        this.note = '';
        this.initialNote = '';
        this.AQuillEditorActionsEnum = AQuillEditorActionsEnum;
        this.noteIsDirty = false;
        this.noteStatus = 'saved';
        this.isSubjectDialogOpen = false;
        this.subjectDialogSubject = null;
        // #endregion
    }
    // #endregion
    // #region Lifecycle Hooks
    mounted() {
        this.initialize();
    }
    async initialize() {
        this.isLoading = true;
        if (this.timeslot && this.timeslot.subjects && this.timeslot.subjects.length > 0) {
            this.goals = this.timeslot.subjects;
        }
        await this.fetchTimeslotNotes();
        this.isLoading = false;
    }
    // #endregion
    // #region Event handlers
    onOpenSubjectDialog(goal) {
        if (!goal) {
            return;
        }
        this.subjectDialogSubject = goal;
        this.isSubjectDialogOpen = true;
    }
    // #endregion
    // #region Class methods
    close() {
        this.$emit('close');
        this.clear();
    }
    clear() {
        this.goals = [];
    }
    getGoalHasPeriod(goal) {
        if (Array.isArray(goal.period)) {
            return goal.period.length > 0;
        }
        return !!goal.period;
    }
    navigateToTimeslotGoalOverview() {
        this.$router.push({
            name: 'goal-planner.timeslot',
            params: { groupId: this.group.id, id: this.timeslot.id },
            query: {
                date: this.date,
                activeStart: this.activeStart,
            },
        });
    }
    checkForEmptyHtml(content) {
        if (!content)
            return true;
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        const textContent = tempDiv.textContent || tempDiv.innerText || '';
        return textContent.trim() === '';
    }
    // #endregion
    // #region Async methods (api calls only)
    async deleteGoal(goal) {
        this.isLoadingGoals = true;
        try {
            await new RpcClient().execute('remove-subject-for-timeslot-for-group', {
                timeslot_id: this.timeslot.id,
                group_id: this.$route.params.id,
                subject_id: goal.id,
            });
            this.goals = this.goals.filter((g) => g.id !== goal.id);
            this.$emit('goal:removed');
        }
        catch (error) {
            ErrorHandler.network(error);
        }
        this.isLoadingGoals = false;
    }
    async deleteTimeslot() {
        if (this.hasGoals) {
            return;
        }
        try {
            await new RpcClient().execute('remove-timeslot-from-group', {
                group_id: this.$route.params.id,
                timeslot_id: this.timeslot.id,
                date: this.date,
            });
            this.$emit('timeslot:removed');
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async onSaveTimeslotNote() {
        if (!this.noteIsDirty) {
            return;
        }
        try {
            await new RpcClient().execute('add-notes-to-timeslot-for-group', {
                timeslot_id: this.timeslot.id,
                group_id: this.group.id,
                date: this.date,
                notes: this.note,
            });
            this.noteStatus = 'saved';
            setTimeout(() => {
                this.noteIsDirty = false;
            }, 3000);
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async fetchTimeslotNotes() {
        try {
            const response = await new RpcClient().execute('get-notes-for-timeslot-and-group', {
                timeslot_id: this.timeslot.id,
                group_id: this.group.id,
                date: this.date,
            });
            this.note = response.notes || '';
            this.initialNote = this.note; // Store initial note to check for changes
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    // #endregion
    // #region Getters & Setters
    get formattedDateTime() {
        const formattedDate = DateTime.fromISO(this.date).toFormat('cccc, dd LLLL yyyy');
        return (formattedDate +
            ' - ' +
            this.timeslot.starts_at.substring(0, 5) +
            '-' +
            this.timeslot.ends_at.substring(0, 5));
    }
    get hasGoals() {
        return this.goals.length > 0;
    }
    get editorOptions() {
        return {
            placeholder: this.$t('Add a note for this timeslot'),
        };
    }
    get studentList() {
        return this.group.getStudents();
    }
    get noteStatusLabel() {
        return this.noteStatus === 'saved' ? this.$tc('Saved') : this.$tc('Unsaved changes');
    }
    // #endregion
    // #region @Watchers
    onNoteChange() {
        const noteHasEmptyHtml = this.checkForEmptyHtml(this.note);
        if (noteHasEmptyHtml && this.initialNote === '') {
            this.noteIsDirty = false;
        }
        else {
            this.noteIsDirty = this.note !== this.initialNote;
        }
    }
    onNoteIsDirtyChange(newValue) {
        this.noteStatus = newValue ? 'dirty' : 'saved';
    }
};
__decorate([
    Prop({ required: true }),
    __metadata("design:type", typeof (_a = typeof GoalPlannerTimeslot !== "undefined" && GoalPlannerTimeslot) === "function" ? _a : Object)
], ManageTimeslotDialog.prototype, "timeslot", void 0);
__decorate([
    Prop({ required: true }),
    __metadata("design:type", String)
], ManageTimeslotDialog.prototype, "date", void 0);
__decorate([
    Prop({ required: true }),
    __metadata("design:type", String)
], ManageTimeslotDialog.prototype, "activeStart", void 0);
__decorate([
    Prop({ required: true }),
    __metadata("design:type", typeof (_b = typeof Group !== "undefined" && Group) === "function" ? _b : Object)
], ManageTimeslotDialog.prototype, "group", void 0);
__decorate([
    Prop(),
    __metadata("design:type", typeof (_c = typeof GroupTemplateFilters !== "undefined" && GroupTemplateFilters) === "function" ? _c : Object)
], ManageTimeslotDialog.prototype, "goalPlannerFilters", void 0);
__decorate([
    Watch('note'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], ManageTimeslotDialog.prototype, "onNoteChange", null);
__decorate([
    Watch('noteIsDirty'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
], ManageTimeslotDialog.prototype, "onNoteIsDirtyChange", null);
ManageTimeslotDialog = __decorate([
    Component({
        components: {
            AQuillEditor,
            SubjectDialog,
        },
    })
], ManageTimeslotDialog);
export default ManageTimeslotDialog;
// #region Enums
// #endregion
// #region Interfaces
// #endregion
// #region Constants
// #endregion
